import React from "react";
import { CompanySubscription } from "../model/company_management_model";
import {
  formatBoolean,
  formatCurrency,
  formatDate,
  formatLabel,
  formatNumber,
  formatPercent,
  getCompanyMeta,
  getCompanyStatusClass,
  getCompanyUserCount,
  getSubscriptionNote,
  getSubscriptionStatusClass,
} from "../view_model/company_management_viewmodel";

interface CompanyManagementDetailsModalProps {
  subscription: CompanySubscription;
  onClose: () => void;
}

const renderSubscriptionBadge = (status: string) => (
  <span
    className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${getSubscriptionStatusClass(
      status,
    )}`}
  >
    {formatLabel(status)}
  </span>
);

const renderCompanyBadge = (isActive?: boolean | null) => (
  <span
    className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${getCompanyStatusClass(
      isActive,
    )}`}
  >
    {isActive === true ? "Active" : isActive === false ? "Inactive" : "Unknown"}
  </span>
);

const renderDetailItem = (label: string, value: React.ReactNode) => {
  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <div className="flex justify-between gap-3">
      <dt>{label}</dt>
      <dd className="max-w-[60%] break-words text-right font-medium text-gray-900">
        {hasValue ? value : "-"}
      </dd>
    </div>
  );
};

const CompanyManagementDetailsModal: React.FC<
  CompanyManagementDetailsModalProps
> = ({ subscription, onClose }) => {
  const companyMeta = getCompanyMeta(subscription);
  const subscriptionNote = getSubscriptionNote(subscription);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {companyMeta?.name || "Company details"}
            </h2>
            {companyMeta?.slug ? (
              <p className="text-sm text-gray-500">{companyMeta.slug}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700">Company</h3>
            <dl className="mt-3 space-y-2 text-sm text-gray-600">
              {renderDetailItem("Website", companyMeta?.website)}
              {renderDetailItem(
                "Total users",
                formatNumber(getCompanyUserCount(subscription)),
              )}
              {renderDetailItem(
                "Total candidates",
                formatNumber(companyMeta?.total_candidates),
              )}
              {renderDetailItem(
                "Total jobs",
                formatNumber(companyMeta?.total_jobs),
              )}
              {renderDetailItem("Company name", companyMeta?.name)}
              {renderDetailItem("Slug", companyMeta?.slug)}
              {renderDetailItem("Industry", companyMeta?.industry)}
              {renderDetailItem("Company size", companyMeta?.company_size)}
              {renderDetailItem("Created", formatDate(companyMeta?.created_at))}
              {renderDetailItem("Updated", formatDate(companyMeta?.updated_at))}
              {renderDetailItem("Status", renderCompanyBadge(companyMeta?.is_active))}
            </dl>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700">Subscription</h3>
            <dl className="mt-3 space-y-2 text-sm text-gray-600">
              {renderDetailItem("Tier type", formatLabel(subscription.tier_type))}
              {renderDetailItem(
                "Billing period",
                formatLabel(subscription.billing_period),
              )}
              {renderDetailItem(
                "Current price",
                formatCurrency(subscription.current_period_price),
              )}
              {renderDetailItem(
                "Subscription status",
                renderSubscriptionBadge(subscription.status),
              )}
              {renderDetailItem(
                "Payment method",
                formatLabel(subscription.payment_method),
              )}
              {renderDetailItem("Auto renew", formatBoolean(subscription.auto_renew))}
              {renderDetailItem("Cancelled at", formatDate(subscription.cancelled_at))}
              {renderDetailItem("Created", formatDate(subscription.created_at))}
              {renderDetailItem("Updated", formatDate(subscription.updated_at))}
              {renderDetailItem("Started", formatDate(subscription.started_at))}
              {renderDetailItem("trial ends at", formatDate(subscription.trial_ends_at))}
              {renderDetailItem("current period start", formatDate(subscription.current_period_start))}
              {renderDetailItem("current period end", formatDate(subscription.current_period_end))}
              {renderDetailItem(
                "Renewal",
                `${subscription.days_until_renewal} days`,
              )}
            </dl>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Credits & Pricing
            </h3>
            <dl className="mt-3 space-y-2 text-sm text-gray-600">
              {renderDetailItem(
                "Balance credits",
                formatNumber(subscription.current_credit_balance ?? 0),
              )}
              {renderDetailItem(
                "Credits per period",
                formatNumber(subscription.credits_per_period),
              )}
              {renderDetailItem(
                "Locked credits per dollar",
                formatNumber(subscription.locked_credits_per_dollar),
              )}
              {renderDetailItem(
                "Locked monthly price",
                formatCurrency(subscription.locked_price_monthly),
              )}
              {renderDetailItem(
                "Locked yearly price",
                formatCurrency(subscription.locked_price_yearly),
              )}
              {renderDetailItem(
                "Locked top-up discount",
                formatPercent(subscription.locked_topup_discount),
              )}
              {renderDetailItem(
                "Locked yearly bonus",
                formatPercent(subscription.locked_yearly_bonus),
              )}
              {renderDetailItem(
                "Locked monthly bonus",
                formatPercent(subscription.locked_monthly_bonus),
              )}
            </dl>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700">
              Scheduled Changes
            </h3>
            <dl className="mt-3 space-y-2 text-sm text-gray-600">
              {renderDetailItem(
                "Cancel at period end",
                formatBoolean(subscription.cancel_at_period_end),
              )}
              {renderDetailItem("Scheduled tier", subscription.scheduled_tier_name)}
              {renderDetailItem(
                "Scheduled billing period",
                formatLabel(subscription.scheduled_billing_period),
              )}
              {renderDetailItem(
                "Scheduled change at",
                formatDate(subscription.scheduled_change_at),
              )}
              {renderDetailItem("Schedule note", subscriptionNote)}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyManagementDetailsModal;
